import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { sortBy, cloneDeep } from 'lodash';
import { GroupResult, groupBy } from '@progress/kendo-data-query';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Guid } from 'guid-typescript';
import { CommunityServiceCategoryService } from 'src/app/core/service/community-service-category.service';

@Component({
  selector: 'app-formly-designer-need-edit',
  templateUrl: './formly-designer-need-edit.component.html',
  styleUrls: ['./formly-designer-need-edit.component.scss']
})
export class FormlyDesignerNeedEditComponent implements OnInit
{
  @Input() public fields: FormlyFieldConfig[];
  @Input() public isDeleted = false;

  @Output() public formChanged: EventEmitter<any> = new EventEmitter();

  public editForm: any;
  public needs: UntypedFormArray;
  public communityServiceCategories = [];
  public groupedCommunityServiceCategories: GroupResult[] = [];

  /**
   * Filtered list of categories for drop-down.
   */
  public categoriesFiltered: GroupResult[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Maximum number of items for cateogies virtualization. This is to accelerate the DOM performance.
   */
  public virtualCategory: any = {
    itemHeight: 30
  };

  public loaded = false;

  constructor(
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private communityServiceCategoryService: CommunityServiceCategoryService) { }

  @Input() public set model(needs: any)
  {
    this.initModel(needs);
  }

  public ngOnInit(): void
  {
    this.loaded = false;
  }

  /**
   * Check if item from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Add a service type entry
   */
  public addNeed(id: number = null, version = 0, serviceCategories: any = null,
    query: any = null, showRules = true, importError = false): void
  {
    this.editForm.controls.needs.push(this.initNeed(id, version, serviceCategories, query, showRules, importError));
    if (id === null)
    {
      this.editForm.markAsDirty();
    }
  }

  /**
   * Remove a service type entry
   *
   * @param i Index
   */
  public removeNeed(i: number): void
  {
    const control = this.editForm.controls.needs;
    control.removeAt(i);
    this.editForm.markAsDirty();
  }

  public showRulesChanged(e: any): void
  {
    this.editForm.controls.needs.controls[e.index].controls.showRules.value = e.show;
    // If the rule is opened, we assumed a state changes.
    if (!this.isDeleted)
    {
      this.editForm.markAsDirty();
      this.formChanged.emit(this.editForm);
    }
  }

  /**
   * Used for the trackBy to prevent rendering the DOM and losing field focus.
   *
   * @param index Row index.
   * @param item Row item.
   */
  public needsTrackBy(index: number, item: any): string
  {
    return item.value.guid;
  }

  /**
   * Filters the catregories based on user input.
   */
  public filterCategories(filter: string): void
  {
    this.searchTerm = filter;
    let categories = cloneDeep(this.groupedCommunityServiceCategories);
    categories.forEach(d =>
    {
      const filteredItems = d.items.filter((item: any) => item.name.toLowerCase().includes(filter.toLowerCase()));
      d.items = filteredItems;
    });

    // Remove groups if there is no items
    categories = categories.filter(x => x.items.length > 0);

    // Set filtered categories
    this.categoriesFiltered = categories;
  }

  /**
   * Model initilization
   *
   * @param needs Assessment needs edit object.
   */
  private initModel(needs: any): void
  {
    if (needs === undefined || this.loaded)
    {
      return;
    }

    // Build form
    this.editForm = this.fb.group({
      needs: this.fb.array([])
    });

    // Get community service categories
    this.communityServiceCategoryService.getCommunityServiceCategoriesList().subscribe({
      next: (categoriesData) =>
      {
        this.communityServiceCategories = categoriesData;
        this.communityServiceCategories.forEach(d =>
        {
          d.serviceTypeName = d.serviceType.displayName;
        });
        this.groupedCommunityServiceCategories = groupBy(this.communityServiceCategories, [{ field: 'serviceTypeName' }]);
        this.groupedCommunityServiceCategories.forEach(d =>
        {
          const items = sortBy(d.items, ['name']);
          d.items = items;
        });
        this.filterCategories('');

        // Set the form
        if (needs && needs.length > 0)
        {
          needs.forEach(data =>
          {
            this.addNeed(data.id, data.version, data.serviceCategories, data.query, false, data.importError);
          });
        }

        this.editForm.valueChanges.subscribe({
          next: () =>
          {
            if (!this.isDeleted)
            {
              this.formChanged.emit(this.editForm);
            }
          }
        });

        // Must apply a delay here because isDeleted is not set yet when setting the model.
        setTimeout(() =>
        {
          this.disableControls();
          this.loaded = true;
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Disable controls when Assessement is deleted
   */
  private disableControls(): void
  {
    if (this.isDeleted)
    {
      this.editForm.controls.needs.controls.forEach(data =>
      {
        data.controls.serviceCategories.disable();
      });
    }
  }

  /**
   * Create a form group.
   */
  private initNeed(id: number, version: number, serviceCategories: any, query: any, showRules: boolean,
    importError: boolean): UntypedFormGroup
  {
    if (query === null)
    {
      query = {
        condition: 'and',
        rules: []
      };
    }
    return this.fb.group({
      guid: Guid.create().toString(),
      id: [id],
      version: [version],
      serviceCategories: [serviceCategories, Validators.required],
      query: [query, Validators.required],
      showRules: [showRules],
      importError: [importError]
    });
  }
}
